/**
 * lastfree.jsx — what an enrolled learner is offered at the end of the last no-fee chapter.
 *
 * FOUNDER, 2026-08-16: "yes add the enrolment invitation at end of chapter 4" — then, correcting
 * the premise it was first built on: "i thought enrolment was even to get free ai course, not
 * anonymous."
 *
 * ── WHAT THAT CHANGED, AND WHY THE FIRST VERSION WAS WORSE ────────────────────────────────────
 * The first draft of this file invited an ANONYMOUS reader to enrol at the end of chapter 4. Under
 * the corrected ruling nobody reaches chapter 4 anonymously — enrolment is what makes somebody a
 * student, and "free" describes the fee. So the reader standing here is already enrolled, already
 * has a record, and being invited to "carry your record" would be the product not knowing who he is.
 *
 * What he is missing is not an identity. It is the sixteen chapters past the free tier. So this is
 * the honest thing at this position: he has finished what costs nothing, here is what is beyond it,
 * and here is what he already has that follows him there.
 *
 * ── IT APPEARS ONCE, AT THE EDGE, AND NOWHERE ELSE ────────────────────────────────────────────
 * `lastFree` is computed from the policy's free_chapters — max(free), never a typed 4 — so a
 * founder ruling that moves the boundary moves this panel with it. A component holding its own copy
 * of that number would be the second-record fault this project has spent the day paying for.
 *
 * ── IT NAMES NO PRICE ─────────────────────────────────────────────────────────────────────────
 * No price is declared for this ladder. A panel that invented "from $X" would be inventing a
 * commercial fact, which is the same offence as inventing a technical one (FP-018). It says what
 * is beyond the boundary and what enrolment already gives; the founder's pricing goes here when it
 * exists.
 */
import { identity } from './identity.mjs';

/* Formatted by the platform rather than a symbol table — see coursemenu.jsx for the argument. */
function money(amount, currency) {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch { return `${currency} ${amount}`; }
}

export default function LastFree({ chapter, lastFree, totalChapters, price = null }) {
  /* Only at the edge, and only for somebody who got here legitimately — which now means signed in.
     An unenrolled visitor never sees this because he never reaches the chapter; the guard stays
     anyway, because a panel whose precondition is enforced elsewhere is a panel that appears in the
     wrong place the day the elsewhere changes. */
  if (!lastFree || chapter !== lastFree) return null;
  if (!identity()) return null;

  const remaining = totalChapters ? totalChapters - lastFree : null;
  if (!remaining || remaining < 1) return null;

  return (
    <div className="g-glass shell-panel learn-lastfree">
      <div className="g-label">The end of the no-fee chapters</div>
      <h3 className="g-h3 lf-h">That was chapter {lastFree} of {totalChapters}</h3>
      <p className="g-body">
        The first {lastFree} chapters of this ladder cost nothing and always will. You have just
        finished the last of them.
      </p>
      {/* WHAT HE ALREADY HAS is said before what he does not, because it is true and because a panel
          that opens with an ask reads as an advertisement. His marks exist and follow him; that is
          what enrolling already bought, and most learners do not know it. */}
      <p className="g-body">
        <b>Your record is already being kept.</b> What you have attempted and what you have passed
        sits against your identity, on any device you sign in from — that is what enrolling did, and
        it does not depend on going any further.
      </p>
      <p className="g-body">
        <b>{remaining} chapters lie beyond this one.</b> They are about what the tool does when
        nobody is watching — bounding a run, hooks, headless, security, working alongside other
        people — and they are where the course stops being about typing and starts being about
        consequence.
      </p>
      {/* ── AND NOW THE NUMBER, WHERE THE ASK ACTUALLY IS ────────────────────────────────────
          The panel named no price until 2026-08-19 because none was ruled, and inventing one would
          have been inventing a commercial fact. One exists now, so it is said plainly and in full:
          the amount, the period, the tax, and what will actually be charged. A paywall that makes a
          learner hunt for the real figure has decided it would rather they did not see it.
          The provisional standing is stated in the same breath and at the same weight — this is the
          surface where under-stating it would cost somebody money. */}
      {price ? (
        <p className="g-body lf-price">
          <b>{money(price.amount, price.currency)}
            {price.period === 'one-time' ? ', once' : ` a ${price.period}`}</b>
          {price.tax && !price.tax.included_in_amount
            ? <> plus {price.tax.rate_percent}% {price.tax.name}
                {price.amount_payable != null
                  ? <> — <b>{money(price.amount_payable, price.currency)}</b> charged</> : null}</>
            : null}
          {price.provisional
            ? <> . This rate is <b>provisional</b>: it is a working figure, not a published tariff,
                and it may change before anyone is billed. Nothing on this page takes payment.</>
            : null}
        </p>
      ) : null}
      <p className="g-body g-dim lf-stay">
        Nothing here expires. Chapters 1 to {lastFree} stay open, and your record stays yours,
        whether you go on or not.
      </p>
    </div>
  );
}
